/**
 * Column template for a raw proxy table.
 *
 * The browser subscribes to tables by key, so the only column metadata it has is the table's
 * `FieldDescriptor`s. This projects them — plus the user's persisted layout and the panel's sort —
 * into the `ColumnTemplate` shape the owned grid adapters consume, and reduces a template back to
 * the persisted layout. Both directions mirror Table's `tableColumnTemplate` /
 * `tableViewDataFromColumnTemplate` so the grid behaves identically across the two surfaces.
 */
import { FieldTypeCategory } from '@thinkalpha/language-services';
import type { ConcreteDataType } from '@thinkalpha/platform-ws-client/contracts/language-services/types.js';
import type { FieldDescriptor, SortModel } from '@thinkalpha/table-client';
import { CategoryForFieldType } from '@thinkalpha/ui-core/util/fields';
import type { ColumnTemplate, TemplateColumn } from '@thinkalpha/ui-types/contracts/column-template';
import { z } from 'zod';

/** Persisted per-source-key column layout; sort lives on the panel view, not here. */
export const tableBrowserColumnStateSchema = z.object({
    columnWidths: z.record(z.string(), z.number()),
    columnOrder: z.array(z.string()).nullable(),
    columnVisibility: z.record(z.string(), z.boolean()),
    pinnedColumns: z.record(z.string(), z.enum(['left', 'right'])).nullable(),
});

export type TableBrowserColumnState = z.infer<typeof tableBrowserColumnStateSchema>;

export const EMPTY_COLUMN_STATE = {
    columnWidths: {},
    columnOrder: null,
    columnVisibility: {},
    pinnedColumns: null,
} satisfies TableBrowserColumnState;

/** Descriptor field types outside every category (context, sequence, formula, …) display as strings. */
export function descriptorDataType(descriptor: FieldDescriptor): ConcreteDataType {
    return CategoryForFieldType.get(descriptor.type) ?? FieldTypeCategory.String;
}

function orderedDescriptors(
    descriptors: readonly FieldDescriptor[],
    columnOrder: readonly string[] | null,
): FieldDescriptor[] {
    if (columnOrder === null) {
        return [...descriptors];
    }
    const rank = new Map(columnOrder.map((id, index) => [id, index] as const));
    // Known columns take their persisted rank; new fields keep descriptor order after them.
    return [...descriptors].sort((a, b) => (rank.get(a.name) ?? Infinity) - (rank.get(b.name) ?? Infinity));
}

/** Projects descriptors plus the persisted layout and the panel's sort into a column template. */
export function descriptorColumnTemplate(
    sourceKey: string,
    descriptors: readonly FieldDescriptor[],
    columnState: TableBrowserColumnState | null,
    sort: SortModel | null,
): ColumnTemplate {
    const state: TableBrowserColumnState = columnState ?? EMPTY_COLUMN_STATE;
    const sortIndexById = new Map(Object.keys(sort ?? {}).map((id, index) => [id, index] as const));
    return {
        name: sourceKey,
        key: 'table',
        isTemplate: false,
        columns: orderedDescriptors(descriptors, state.columnOrder).map((descriptor): TemplateColumn => {
            const direction = sort?.[descriptor.name];
            return {
                id: descriptor.name,
                name: descriptor.name,
                type: 'indicator',
                indicator: { id: descriptor.name },
                dataType: descriptorDataType(descriptor),
                unit: null,
                visible: state.columnVisibility[descriptor.name] ?? true,
                width: state.columnWidths[descriptor.name],
                pinned: state.pinnedColumns?.[descriptor.name],
                sort: direction === 'ascending' ? 'asc' : direction === 'descending' ? 'desc' : undefined,
                sortIndex: sortIndexById.get(descriptor.name),
                displaySettings: [],
            };
        }),
        gridDisplaySettings: [],
        rowBasedDisplaySettings: [],
    };
}

/** Reduces a template the grid updated back to the persisted layout (order, widths, visibility, pins). */
export function columnStateFromTemplate(template: ColumnTemplate): TableBrowserColumnState {
    const columnWidths: Record<string, number> = {};
    const columnVisibility: Record<string, boolean> = {};
    const pinnedColumns: Record<string, 'left' | 'right'> = {};
    for (const column of template.columns) {
        if (column.width !== undefined) {
            columnWidths[column.id] = column.width;
        }
        if (column.visible === false) {
            columnVisibility[column.id] = false;
        }
        if (column.pinned !== undefined) {
            pinnedColumns[column.id] = column.pinned;
        }
    }
    return {
        columnWidths,
        columnOrder: template.columns.map((column) => column.id),
        columnVisibility,
        pinnedColumns: Object.keys(pinnedColumns).length === 0 ? null : pinnedColumns,
    };
}

/** Reads the template's ordered sort back into the wire sort model; null when unsorted. */
export function sortModelFromTemplate(template: ColumnTemplate): SortModel | null {
    const sorted = template.columns
        .filter((column) => column.sort !== undefined)
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
    if (sorted.length === 0) {
        return null;
    }
    return Object.fromEntries(sorted.map((column) => [column.id, column.sort === 'asc' ? 'ascending' : 'descending']));
}
